import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Pagination } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLangPath } from "./hooks/useLangPath";
import articlesData from "./articles/articles.json";
import Seo from "./Seo";
import "./Articles.css";

function Articles() {
  const { t } = useTranslation();
  const langPath = useLangPath();
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 100;

  // Daily recaps come from a STATIC index generated nightly next to the recap
  // markdown — a cached hosting fetch, not a database read. The React bundle
  // never has to be rebuilt for a new recap to appear here.
  const [recaps, setRecaps] = useState([]);
  useEffect(() => {
    let live = true;
    fetch("/articles/recaps/index.json")
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        if (!live || !Array.isArray(list)) return;
        setRecaps(list.map((e) => ({
          slug: e.slug,
          date: e.date,
          recap: true,
          file: `recaps/${e.slug}.md`,
          title: `Daily Market Recap — ${e.date}`,
          header: "AI news-sentiment recap across all instruments.",
        })));
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const reversedArticlesData = [...recaps, ...[...articlesData].reverse()];
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = reversedArticlesData.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(reversedArticlesData.length / articlesPerPage);

  const paginationControls = totalPages > 1 && (
    <div className="pagination-container">
      <Pagination>
        {[...Array(totalPages).keys()].map((number) => (
          <Pagination.Item
            key={number + 1}
            active={number + 1 === currentPage}
            onClick={() => { setCurrentPage(number + 1); window.scrollTo(0, 0); }}
          >
            {number + 1}
          </Pagination.Item>
        ))}
      </Pagination>
    </div>
  );

  return (
    <Container className="articles-container">
      <Seo
        title={t("articles.pageTitle")}
        description={t("articles.pageDesc")}
        canonical="/articles"
        breadcrumbs={[
          { name: t("common.home"), path: "/" },
          { name: t("articles.h1"), path: "/articles" },
        ]}
      />
      <h1>{t("articles.h1")}</h1>
      <p>{t("articles.intro")}</p>
      {paginationControls}
      <Row>
        {currentArticles.map((article, index) => {
          const ai = articlesData.findIndex(a => a.slug === article.slug) + 1;
          const title = article.recap
            ? t("articles.recapTitle", { date: article.date, defaultValue: article.title })
            : t(`articles.a${ai}title`, { defaultValue: article.title });
          const header = article.recap
            ? t("articles.recapHeader", { defaultValue: article.header })
            : t(`articles.a${ai}header`, { defaultValue: article.header });
          return (
            <Col key={index} md={4}>
              <Card className="mb-4">
                <Card.Body>
                  <Card.Title>{title}</Card.Title>
                  <Card.Text>{header}</Card.Text>
                  <Card.Text>
                    <small className="text-muted">{article.date}</small>
                  </Card.Text>
                  <Link to={langPath(`/articles/${article.slug}`)}>{title}</Link>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
      {paginationControls}
    </Container>
  );
}

export default Articles;
